import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatRp } from "@/lib/api";
import { Wallet, ArrowDownToLine, CheckCircle2, Clock, XCircle } from "lucide-react";

export default function SellerWallet() {
  const [wallet, setWallet] = useState({ balance: 0, paid: 0, pending: 0, history: [] });
  const [form, setForm] = useState({ amount: 0, bank_name: "", bank_account: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const r = await api.get("/seller/wallet"); setWallet(r.data); }
    catch { toast.error("Gagal load wallet"); }
  };
  useEffect(() => { load(); }, []);

  const request = async () => {
    if (!form.amount || form.amount <= 0) { toast.error("Isi jumlah"); return; }
    if (form.amount > wallet.balance) { toast.error("Melebihi saldo"); return; }
    setLoading(true);
    try {
      await api.post("/seller/withdraw", { ...form, amount: parseInt(form.amount) });
      toast.success("Permintaan withdrawal terkirim");
      setForm({ amount: 0, bank_name: "", bank_account: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    setLoading(false);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="grid sm:grid-cols-3 gap-4">
          <Card className="glass glow-border bg-transparent rounded-xl p-5">
            <div className="text-[10px] font-mono-metric uppercase tracking-widest text-cyan-400">SALDO</div>
            <div data-testid="wallet-balance" className="mt-2 font-mono-metric text-2xl font-bold text-emerald-300">{formatRp(wallet.balance)}</div>
          </Card>
          <Card className="glass glow-border bg-transparent rounded-xl p-5">
            <div className="text-[10px] font-mono-metric uppercase tracking-widest text-cyan-400">PENDING</div>
            <div className="mt-2 font-mono-metric text-2xl font-bold text-amber-300">{formatRp(wallet.pending)}</div>
          </Card>
          <Card className="glass glow-border bg-transparent rounded-xl p-5">
            <div className="text-[10px] font-mono-metric uppercase tracking-widest text-cyan-400">TERBAYAR</div>
            <div className="mt-2 font-mono-metric text-2xl font-bold text-white">{formatRp(wallet.paid)}</div>
          </Card>
        </div>
        <Card className="glass glow-border bg-transparent rounded-2xl p-6">
          <h3 className="font-display font-bold text-white text-lg mb-4">Riwayat Withdrawal</h3>
          <div className="space-y-2">
            {wallet.history.length === 0 && <div className="text-slate-500 text-sm">Belum ada withdrawal.</div>}
            {wallet.history.map(w => (
              <div key={w._id || w.requested_at} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 flex justify-between items-center flex-wrap gap-2">
                <div>
                  <div className="text-white text-sm font-mono-metric">{formatRp(w.amount)}</div>
                  <div className="text-xs text-slate-400">{w.bank_name} · {w.bank_account}</div>
                  <div className="text-[10px] text-slate-500 font-mono-metric">{new Date(w.requested_at).toLocaleString("id-ID")}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded font-mono-metric ${w.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : w.status === "rejected" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>
                  {w.status === "paid" ? <CheckCircle2 className="w-3 h-3 inline mr-1"/> : w.status === "rejected" ? <XCircle className="w-3 h-3 inline mr-1"/> : <Clock className="w-3 h-3 inline mr-1"/>}
                  {w.status.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <div>
        <Card className="glass glow-border bg-transparent rounded-2xl p-6">
          <h3 className="font-display font-bold text-white text-lg mb-4"><ArrowDownToLine className="w-5 h-5 inline mr-1"/>Tarik Saldo</h3>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Jumlah (Rp)</Label>
              <Input data-testid="wd-amount" type="number" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})} className="mt-1 bg-slate-900/60 border-slate-700 text-white h-11 font-mono-metric"/>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Bank</Label>
              <Input data-testid="wd-bank" value={form.bank_name} onChange={e=>setForm({...form, bank_name:e.target.value})} placeholder="BCA / Mandiri / BSI" className="mt-1 bg-slate-900/60 border-slate-700 text-white h-11"/>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">No. Rekening</Label>
              <Input data-testid="wd-rek" value={form.bank_account} onChange={e=>setForm({...form, bank_account:e.target.value})} className="mt-1 bg-slate-900/60 border-slate-700 text-white h-11 font-mono-metric"/>
            </div>
            <Button data-testid="wd-submit" onClick={request} disabled={loading} className="w-full h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold">{loading ? "Mengirim..." : "Ajukan Withdrawal"}</Button>
            <div className="text-[10px] text-slate-500 font-mono-metric text-center">Diproses admin dalam 1–3 hari kerja</div>
          </div>
        </Card>
      </div>
    </div>
  );
}
